<script setup>
/**
 * One number on the Do not contact list, and — the actual job of this row — why it is
 * there and whether that is yours to change.
 *
 * FOUR REASONS, TWO BEHAVIOURS, AND THE ROW HAS TO MAKE THAT VISIBLE. `manual` (someone
 * on the team typed it here) and `api` (the owner's own system posted it) are the
 * workspace's own decisions and it may lift them. `keyword` (the customer typed STOP) and
 * `provider` (WhatsApp's own opt-out, the one that damages the sender's quality rating)
 * are the customer's decision, and `DELETE /v1/outbound/suppressions/:phone` answers 403
 * `opt_out_not_removable` for both.
 *
 * SO THE BUTTON IS NOT RENDERED ON THOSE TWO, and the rule for which is which is READ
 * from `WASLO_SUPPRESSION_REMOVABLE_REASONS` rather than re-listed here. A second copy of
 * a two-item list is a second place for `api` to go missing — which is exactly what
 * happened to it once already, in the plan's three-value version of this same list.
 *
 * WHAT STANDS WHERE THE BUTTON WOULD BE on the locked two is a marker saying it is not
 * the workspace's to lift; the sentence naming the one thing that WOULD undo it — the
 * customer sending START to the business's number — is in the reason's own body text, one
 * line up, where it is read as part of the explanation rather than as an error. Saying it
 * twice on one row was drafted and reads as a warning about something the owner did.
 *
 * The row NEVER pre-decides a refusal it has not been told about: a removable row still
 * goes through the API, which is the only authority, and the page renders what it says.
 * This component only declines to OFFER what is already known to be refused.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../../i18n.js';
import { MARKER, isolateParts } from '../../helpers/bidi.js';
import { relativeTime } from '../../helpers/relative-time.js';
import { WASLO_SUPPRESSION_REMOVABLE_REASONS } from '../../api/client.js';

const props = defineProps({
  /** A row from `wasloApi.getOutboundNumbers()`. */
  row: { type: Object, required: true },
  /** This row's own removal is in flight. */
  isRemoving: { type: Boolean, default: false },
  /** Some removal is in flight, on this row or another one. */
  isBusy: { type: Boolean, default: false },
});

const emit = defineEmits(['remove']);

const { t, locale } = useWasloI18n();

const key = name => `WASLO.OUTBOUND.NUMBERS.${name}`;

/**
 * Reason → copy and mark. A map rather than `reason.toUpperCase()` so a value the client
 * ever starts admitting cannot silently interpolate into a key that does not exist; the
 * fallback is the one the strings already carry for it.
 */
const REASON_COPY = Object.freeze({
  manual: { label: 'MANUAL', body: 'MANUAL_BODY' },
  api: { label: 'API', body: 'API_BODY' },
  keyword: { label: 'KEYWORD', body: 'KEYWORD_BODY' },
  provider: { label: 'PROVIDER', body: 'PROVIDER_BODY' },
});

const copy = computed(
  () => REASON_COPY[props.row.reason] ?? { label: 'UNKNOWN', body: '' }
);

// `REASON` is a SIBLING of `NUMBERS`, not a child of it — it is shared with the Log,
// which reads the other half of that tree (`REASON.MESSAGE.*`, why one send was stopped,
// a different vocabulary that happens to look like this one). So these two keys do not go
// through `key()`, and a first draft that assumed they did rendered four raw key strings.
const reasonLabel = computed(() =>
  t(`WASLO.OUTBOUND.REASON.LIST.${copy.value.label}`)
);

const reasonBody = computed(() =>
  copy.value.body ? t(`WASLO.OUTBOUND.REASON.LIST.${copy.value.body}`) : ''
);

/** Non-null means it WAS on the list and is not any more. History, not a live block. */
const isRemoved = computed(() => props.row.removedAt !== null);

const isLocked = computed(
  () => !WASLO_SUPPRESSION_REMOVABLE_REASONS.includes(props.row.reason)
);

/** Only an active row that the workspace owns can be taken off. */
const canRemove = computed(() => !isLocked.value && !isRemoved.value);

const when = iso =>
  relativeTime(iso, locale.value, {
    justNow: t('WASLO.OUTBOUND.COMMON.JUST_NOW'),
  });

/**
 * ONE STRING, ONE NODE, ONE DIRECTION — the rule `ConnectionsPage` writes out in full.
 * Both halves are translated relative times with no user content in them, so they are
 * safe to join; `removedBy` is not, and gets its own isolated line below.
 */
const metaLine = computed(() => {
  const added = when(props.row.createdAt);
  const taken = isRemoved.value ? when(props.row.removedAt) : null;
  return [
    added ? t(key('ROW.ADDED_WHEN'), { when: added }) : '',
    taken ? t(key('ROW.REMOVED_WHEN'), { when: taken }) : '',
  ]
    .filter(Boolean)
    .join(' · ');
});

/**
 * Who took it off. Whatever the server recorded — a key id, an operator — so it goes in a
 * real `<bdi>` isolate rather than into an interpolated sentence that the bidi algorithm
 * is free to rearrange around it.
 */
const removedBy = computed(() =>
  props.row.removedBy === null
    ? null
    : isolateParts(
        t(key('ROW.REMOVED_BY'), { who: MARKER }),
        props.row.removedBy
      )
);
</script>

<template>
  <!-- `flex` on the `<li>` is load-bearing: a bare list item keeps `display: list-item`
       and renders a bullet in the page gutter. `SourceCard` found this the slow way. -->
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start"
    :data-waslo-number="row.id"
    :data-waslo-number-reason="row.reason"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9 bg-n-alpha-2"
      aria-hidden="true"
    >
      <!-- Icon, not colour. A row on this list is a fact rather than a fault, and an
           amber mark on a customer's own opt-out would spend the page's one warning
           colour on the state where nothing is wrong (README §4). -->
      <span
        class="size-4 text-n-slate-10"
        :class="isLocked ? 'i-lucide-lock' : 'i-lucide-user-round-x'"
      />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <!-- The number itself, in an isolate. It is the customer's, it is rendered inside a
           row whose surrounding text may be Arabic, and a leading `+` is a neutral that
           an adjacent RTL run will otherwise absorb and move to the far end. -->
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-number-phone
      >
        <bdi>{{ row.phone }}</bdi>
      </p>

      <p class="mb-0 text-sm font-medium text-n-slate-11" data-waslo-number-why>
        {{ reasonLabel }}
      </p>

      <!-- The sentence that makes the four reasons different from each other, including
           what would undo the two this workspace cannot. -->
      <p
        v-if="reasonBody"
        class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
        data-waslo-number-explain
      >
        {{ reasonBody }}
      </p>

      <p
        v-if="metaLine"
        class="mb-0 text-xs text-n-slate-10"
        dir="auto"
        data-waslo-number-meta
      >
        {{ metaLine }}
      </p>

      <p
        v-if="removedBy"
        class="mb-0 text-xs text-n-slate-10"
        dir="auto"
        data-waslo-number-removed-by
      >
        <span>{{ removedBy.before }}</span
        ><bdi>{{ removedBy.value }}</bdi
        ><span>{{ removedBy.after }}</span>
      </p>
    </div>

    <!-- The same slot, in the same order, on every row: exactly one of a badge, a locked
         marker or a button. `flex-wrap` because at panel width a 30-character marker and
         a 9-unit mark do not share a line (README §1). -->
    <div
      class="flex flex-wrap items-center gap-2 min-w-0 shrink-0 sm:ms-auto ms-0"
    >
      <span
        v-if="isRemoved"
        class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
        data-waslo-number-removed-badge
      >
        {{ t(key('ROW.REMOVED_BADGE')) }}
      </span>

      <Button
        v-else-if="canRemove"
        :label="t(key('REMOVE.ACTION'))"
        variant="faded"
        color="ruby"
        size="sm"
        :is-loading="isRemoving"
        :disabled="isBusy"
        data-waslo-number-remove
        @click="emit('remove', row)"
      />

      <!-- Not a disabled button. A control that can never be pressed is still a control,
           and it invites the press that would come back 403. -->
      <span
        v-else
        class="flex items-center gap-2 text-xs min-w-0 text-n-slate-10"
        data-waslo-number-locked
      >
        <span class="shrink-0 size-3.5 i-lucide-lock" aria-hidden="true" />
        {{ t(key('LOCKED.HEADING')) }}
      </span>
    </div>
  </li>
</template>
